/**
 * section-ingredients-accordion.js
 * Accessible accordion for the Ingredients section.
 *
 * Keyboard support (WCAG 2.2 / APG accordion pattern):
 *   Enter / Space — toggle the focused accordion item
 *   Arrow Down    — move focus to next trigger
 *   Arrow Up      — move focus to previous trigger
 *   Home          — move focus to first trigger
 *   End           — move focus to last trigger
 *
 * Only one panel is expanded at a time (single-select).
 *
 * Video support:
 *   - Blocks with a video_url render a <video> instead of <img>.
 *   - The `autoplay` HTML attribute is the SOURCE OF TRUTH for whether
 *     JS manages playback. Videos without autoplay are left completely
 *     untouched, so their poster remains visible (avoids Safari's
 *     "first-frame replaces poster" bug on subsequent loads).
 *   - Active autoplay-videos play; inactive autoplay-videos pause.
 *   - The custom pause/play button appears only when the active slide
 *     is an autoplay-video (WCAG 2.2.2 — Pause, Stop, Hide). For
 *     videos with native controls, the controls satisfy this criterion.
 *   - prefers-reduced-motion: reduce — autoplay-videos are kept paused;
 *     poster frame shows.
 */

(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** Boot every instance on the page (supports multiple sections). */
  function init() {
    document
      .querySelectorAll('[data-ingredients-accordion]')
      .forEach(setupAccordion);
  }

  /**
   * @param {HTMLElement} root — the [data-ingredients-accordion] wrapper
   */
  function setupAccordion(root) {
    var triggers = Array.from(
      root.querySelectorAll('.ingredients-accordion__trigger')
    );
    var items = Array.from(
      root.querySelectorAll('.ingredients-accordion__item')
    );
    var pauseBtns = Array.from(
      root.querySelectorAll('[data-ia-pause]')
    );

    if (!triggers.length) return;

    /* ---- Open the first item by default ---- */
    activateItem(items, triggers, 0, root, pauseBtns);

    /* ---- Click handler (delegated) ---- */
    root.addEventListener('click', function (e) {
      var trigger = e.target.closest('.ingredients-accordion__trigger');
      if (!trigger) return;
      var index = triggers.indexOf(trigger);
      if (index === -1) return;
      activateItem(items, triggers, index, root, pauseBtns);
    });

    /* ---- Keyboard handler ---- */
    root.addEventListener('keydown', function (e) {
      var trigger = e.target.closest('.ingredients-accordion__trigger');
      if (!trigger) return;

      var index = triggers.indexOf(trigger);
      var last = triggers.length - 1;
      var next;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          next = index < last ? index + 1 : 0;
          triggers[next].focus();
          break;

        case 'ArrowUp':
          e.preventDefault();
          next = index > 0 ? index - 1 : last;
          triggers[next].focus();
          break;

        case 'Home':
          e.preventDefault();
          triggers[0].focus();
          break;

        case 'End':
          e.preventDefault();
          triggers[last].focus();
          break;

        /* Enter / Space are handled by the browser click on <button>. */
      }
    });

    /* ---- Pause / Play button handler ---- */
    pauseBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var wrap = btn.closest(
          '.ingredients-accordion__image-wrap, .ingredients-accordion__mobile-media-wrap'
        );
        if (!wrap) return;

        var activeVideo = wrap.querySelector('.ingredients-accordion__video.is-active');
        if (!activeVideo) return;

        if (activeVideo.paused) {
          safePlay(activeVideo);
          btn.classList.remove('is-paused');
          btn.setAttribute('aria-label', 'Pause background animation');
        } else {
          activeVideo.pause();
          btn.classList.add('is-paused');
          btn.setAttribute('aria-label', 'Play background animation');
        }
      });
    });

    /* ---- Respond to reduced-motion changes at runtime ---- */
    prefersReducedMotion.addEventListener('change', function () {
      /* Re-evaluate the current active index and pause/play accordingly */
      var activeIndex = 0;
      items.forEach(function (item, i) {
        if (item.getAttribute('data-active') === 'true') activeIndex = i;
      });
      syncVideoPlayback(root, activeIndex, pauseBtns);
    });
  }

  /**
   * Expand item at `activeIndex`, collapse the rest, swap images/videos.
   */
  function activateItem(items, triggers, activeIndex, root, pauseBtns) {
    items.forEach(function (item, i) {
      var trigger = triggers[i];
      var panel = item.querySelector('.ingredients-accordion__panel');
      var isActive = i === activeIndex;

      item.setAttribute('data-active', isActive ? 'true' : 'false');
      trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');

      if (isActive) {
        /* Expand: measure scrollHeight then set max-height */
        panel.style.maxHeight = panel.scrollHeight + 'px';
      } else {
        panel.style.maxHeight = '0';
      }
    });

    /* ---- Swap images / videos (desktop + mobile) ---- */
    var allDesktopMedia = root.querySelectorAll(
      '.ingredients-accordion__media .ingredients-accordion__img'
    );
    var allMobileMedia = root.querySelectorAll(
      '.ingredients-accordion__mobile-image .ingredients-accordion__img'
    );

    allDesktopMedia.forEach(function (el, i) {
      el.classList.toggle('is-active', i === activeIndex);
    });
    allMobileMedia.forEach(function (el, i) {
      el.classList.toggle('is-active', i === activeIndex);
    });

    /* ---- Manage video playback & pause button visibility ---- */
    syncVideoPlayback(root, activeIndex, pauseBtns);
  }

  /**
   * Wrapper for HTMLMediaElement.play() that swallows the AbortError /
   * NotAllowedError rejection some browsers throw when autoplay is
   * blocked or play() is interrupted by a subsequent pause().
   */
  function safePlay(video) {
    var p = video.play();
    if (p && typeof p.then === 'function') {
      p.catch(function () { /* autoplay blocked — leave element paused */ });
    }
  }

  /**
   * Play the active autoplay-video (if any), pause inactive autoplay-
   * videos, and show/hide the custom pause button accordingly.
   *
   * IMPORTANT: videos WITHOUT the `autoplay` attribute are never
   * touched here. Calling .play() / .pause() on a controls-only video
   * forces Safari to load the first frame, which replaces the poster
   * image — a known WebKit quirk. Treating `autoplay` as the source of
   * truth keeps the poster visible across reloads.
   */
  function syncVideoPlayback(root, activeIndex, pauseBtns) {
    var allVideos = root.querySelectorAll('[data-ia-video]');
    var reducedMotion = prefersReducedMotion.matches;

    /* Pause inactive autoplay-videos so they don't keep playing in the
       background after the user activates another slide. Skip videos
       without autoplay — they are user-controlled and we don't want
       to interfere with their poster or playback state. */
    allVideos.forEach(function (video) {
      if (video.autoplay && !video.paused) {
        video.pause();
      }
    });

    /* Determine whether the active slide has a video */
    var desktopMedia = root.querySelectorAll(
      '.ingredients-accordion__media .ingredients-accordion__img'
    );
    var mobileMedia = root.querySelectorAll(
      '.ingredients-accordion__mobile-image .ingredients-accordion__img'
    );

    var activeDesktop = desktopMedia[activeIndex];
    var activeMobile  = mobileMedia[activeIndex];

    var desktopIsVideo = activeDesktop && activeDesktop.matches('[data-ia-video]');
    var mobileIsVideo  = activeMobile && activeMobile.matches('[data-ia-video]');

    /* Play the active video ONLY if it has the autoplay attribute and
       reduced-motion is not preferred. Controls-only videos are left
       alone — the user starts them via the native play button. */
    if (!reducedMotion) {
      if (desktopIsVideo && activeDesktop.autoplay) safePlay(activeDesktop);
      if (mobileIsVideo && activeMobile.autoplay) safePlay(activeMobile);
    }

    /* Custom pause button is only relevant for autoplay-videos. When
       the active slide uses native controls, the controls themselves
       satisfy WCAG 2.2.2, so we hide the custom button. */
    var activeIsAutoplay =
      (desktopIsVideo && activeDesktop.autoplay) ||
      (mobileIsVideo && activeMobile.autoplay);

    pauseBtns.forEach(function (btn) {
      if (activeIsAutoplay && !reducedMotion) {
        btn.removeAttribute('hidden');
        btn.classList.remove('is-paused');
        btn.setAttribute('aria-label', 'Pause background animation');
      } else {
        btn.setAttribute('hidden', '');
      }
    });
  }

  /* ---- Boot ---- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();